import React from 'react';
import { Box, Flex, Text } from 'pounceV2';
import { useTheme } from '@mui/material';
import { useInputNavigationContext } from 'Components/CompoundInput/InputNavigationContext/InputNavigationContext';
import { WhitespaceText, useGlyphColor, useQuoteStyle, useTransparentMonoInputSx } from '.';

export interface QuotedTextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onSelect' | 'ref' | 'value'
> {
  value?: string;
  /** Forwarded to the input as `aria-invalid`; surfaced visually by the parent
   *  CompoundInputV3 container's error chrome. */
  invalid?: boolean;
  'data-tid'?: string;
}

// Scalar-value variant of TagsValueInputV3's chrome: curly quotes around the
// value plus a visible whitespace-glyph overlay behind a transparent input.
// Used inside CompoundInputV3 filter rows for EQUALS/CONTAINS/etc. so the
// visual symbolism (quotes + glyphs) matches the IN-list editor's symbolism
// within the same container.
//
// Underlying value is RAW (no trim, no transformation). Sibling navigation in
// the parent CompoundInputV3 is preserved via InputNavigationContext — when
// the context is absent (standalone use), navigation gracefully degrades to a
// plain input.
const QuotedTextInputV3 = React.forwardRef<HTMLInputElement, QuotedTextInputProps>(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression for displayName
  function QuotedTextInputV3(
    { value, disabled = false, placeholder, onKeyDown, invalid, ...rest }: QuotedTextInputProps,
    ref,
  ) {
    const {
      palette: { componentTokens },
    } = useTheme();
    const navContext = useInputNavigationContext();

    const quoteSx = useQuoteStyle();
    const glyphColor = useGlyphColor();
    const inputSx = useTransparentMonoInputSx();
    const inputTextColor = componentTokens.tags.colors.condition.text.labelBase;

    const stringValue = value ?? '';
    const hasValue = stringValue.length > 0;

    // Measurer content sizes the wrapper to match the input's current text. We
    // render the placeholder invisibly when empty so the wrapper picks up the
    // placeholder's width without double-rendering (the input shows its own
    // browser-native placeholder via the ::placeholder style above).
    let measurer: React.ReactNode;
    if (hasValue) {
      measurer = <WhitespaceText value={stringValue} glyphColor={glyphColor} />;
    } else if (placeholder) {
      measurer = (
        <Box as="span" visibility="hidden">
          {placeholder}
        </Box>
      );
    } else {
      measurer = ' ';
    }

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (navContext) {
          const inputElement = e.currentTarget;
          const { selectionStart, selectionEnd } = inputElement;
          const atStart = selectionStart === 0 && selectionEnd === 0;
          const atEnd =
            selectionStart === stringValue.length && selectionEnd === stringValue.length;

          if ((e.key === 'Backspace' || e.key === 'ArrowLeft') && atStart) {
            navContext.focusPreviousInput(inputElement);
            e.preventDefault();
          }
          if (e.key === 'ArrowRight' && atEnd) {
            navContext.focusNextInput(inputElement);
            e.preventDefault();
          }
        }
        onKeyDown?.(e);
      },
      [navContext, onKeyDown, stringValue.length],
    );

    // Quotes are always rendered so their width is reserved in the row layout —
    // toggling visibility (not presence) avoids a first-keystroke layout shift.
    const quoteVisibility = hasValue ? 'visible' : 'hidden';

    return (
      <Flex as="span" align="baseline" gap="2px" cursor={disabled ? 'not-allowed' : 'text'}>
        <Text as="span" sx={quoteSx} visibility={quoteVisibility} aria-hidden="true">
          &ldquo;
        </Text>
        <Box position="relative" display="inline-block">
          <Box
            as="span"
            display="inline-block"
            whiteSpace="pre"
            fontFamily="monospace"
            fontSize="medium"
            fontWeight="medium"
            color={inputTextColor}
            aria-hidden="true"
          >
            {measurer}
          </Box>
          <Box
            as="input"
            ref={ref}
            type="text"
            value={stringValue}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={invalid || undefined}
            backgroundColor="transparent"
            border="0"
            p={0}
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            width="100%"
            height="100%"
            fontFamily="monospace"
            fontSize="medium"
            fontWeight="medium"
            color="transparent"
            sx={inputSx}
            onKeyDown={handleKeyDown}
            {...rest}
            {...(navContext?.dataAttribute ?? {})}
          />
        </Box>
        <Text as="span" sx={quoteSx} visibility={quoteVisibility} aria-hidden="true">
          &rdquo;
        </Text>
      </Flex>
    );
  },
);

export default QuotedTextInputV3;
